import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Stack, CircularProgress } from '@mui/material';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import { useConfirm } from 'material-ui-confirm';

import { AppContext } from '../../../../../../context';
import { translate as t } from 'hooks/useTranslations';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import useGroup from '../../../_data_sources/useGroup';
import useManual from '../../../_data_sources/useManual';
import Loading from 'views/App/Portal/Loading';
import errorHelper from 'helpers/errorHelper';
import ToCSelector from './_components/ToCSelector';
import ToCDrawer from './_components/ToCDrawer';
import { ToCItemData } from './_components/ToCItem';
import { buildToCData } from './_helpers/buildToCData';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';

export default function Exclusions() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { currentOrganizationId } = useContext(AppContext);
  const { id: groupId, manualId } = useParams<{ id: string; manualId: string; }>();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [initialSelectedIds, setInitialSelectedIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  const groupQuery = useGroup(currentOrganizationId || '', groupId);
  const manualQuery = useManual(manualId);
  const { getLock, removeLock, update: updateManual } = useManageManual();
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize selected items based on existing exclusion groups in the manual
  useEffect(() => {
    if (!manualQuery.data || !groupId || isInitialized) return;

    const initialIds = new Set<string>();

    // Check prefaces for matching exclusion groups
    manualQuery.data.prefaces.forEach((preface: any) => {
      if (preface.exclusionGroups?.some((group: any) => group.id === groupId)) {
        initialIds.add(preface.id);
      }
    });

    // Check policies for matching exclusion groups
    manualQuery.data.chapters.forEach((chapter: any) => {
      chapter.policies?.forEach((policy: any) => {
        if (policy.exclusionGroups?.some((group: any) => group.id === groupId)) {
          initialIds.add(policy.id);
        }
      });
    });

    setSelectedIds(initialIds);
    setInitialSelectedIds(initialIds);
    setIsInitialized(true);
  }, [manualQuery.data, groupId, isInitialized]);

  const backUrl = `/admin/lexid/groups/${groupId}#tab=manuals`;

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (selectedIds.size !== initialSelectedIds.size) return true;

    for (const id of selectedIds) {
      if (!initialSelectedIds.has(id)) return true;
    }

    return false;
  }, [selectedIds, initialSelectedIds]);

  const handleClose = useCallback(async () => {
    if (hasUnsavedChanges) {
      try {
        await confirm({
          description: t('groups.manuals.exclusions.confirmCancel'),
        });
        navigate(backUrl);
      } catch {
        // User cancelled, do nothing
      }
    } else {
      navigate(backUrl);
    }
  }, [hasUnsavedChanges, confirm, navigate, backUrl]);

  const handleSave = useCallback(async () => {
    if (!manualId || !groupId || !manualQuery.data) return;

    setIsSaving(true);

    try {
      // Step 1: Get the manual lock
      await new Promise<void>((resolve, reject) => {
        getLock(
          manualId,
          () => resolve(),
          (error) => reject(error)
        );
      });

      // Step 2: Fetch fresh manual data
      const { data: freshManualData } = await manualQuery.refetch();

      if (!freshManualData) {
        throw new Error('Failed to fetch fresh manual data');
      }

      // Step 3: Update exclusionGroup attribute for selected/deselected items
      const updatedManual: any = {
        ...freshManualData,
        prefaces: freshManualData.prefaces.map((preface: any) => {
          const existingGroups = preface.exclusionGroups || [];
          const hasCurrentGroup = existingGroups.some((group: any) => group.id === groupId);
          const isSelected = selectedIds.has(preface.id);

          let updatedGroups = existingGroups;

          // Add current group if selected and not already present
          if (isSelected && !hasCurrentGroup) {
            updatedGroups = [...existingGroups, { id: groupId }];
          }
          // Remove current group if not selected but present
          else if (!isSelected && hasCurrentGroup) {
            updatedGroups = existingGroups.filter((group: any) => group.id !== groupId);
          }

          return {
            ...preface,
            exclusionGroups: updatedGroups,
          };
        }),
        chapters: freshManualData.chapters.map((chapter: any) => ({
          ...chapter,
          policies: chapter.policies.map((policy: any) => {
            const existingGroups = policy.exclusionGroups || [];
            const hasCurrentGroup = existingGroups.some((group: any) => group.id === groupId);
            const isSelected = selectedIds.has(policy.id);

            let updatedGroups = existingGroups;

            // Add current group if selected and not already present
            if (isSelected && !hasCurrentGroup) {
              updatedGroups = [...existingGroups, { id: groupId }];
            }
            // Remove current group if not selected but present
            else if (!isSelected && hasCurrentGroup) {
              updatedGroups = existingGroups.filter((group: any) => group.id !== groupId);
            }

            return {
              ...policy,
              exclusionGroups: updatedGroups,
            };
          }),
        })),
      };

      // Step 4: Update the manual
      await new Promise<void>((resolve, reject) => {
        updateManual(
          manualId,
          updatedManual,
          () => resolve(),
          (error) => reject(error)
        );
      });

      // Step 5: Release the lock
      await new Promise<void>((resolve, reject) => {
        removeLock(
          manualId,
          () => resolve(),
          (error) => reject(error)
        );
      });

      // Success notification
      enqueueSnackbar(t('groups.manuals.exclusions.saveSuccess'), { variant: 'success' });

      // Reset initial state to current selections (no unsaved changes after successful save)
      setInitialSelectedIds(new Set(selectedIds));

      // Invalidate queries
      await queryClient.invalidateQueries(['manual', manualId]);

      // Remove focus from active element to prevent aria-hidden accessibility warnings
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      // Navigate back to Policy Manuals tab
      navigate(backUrl);
    } catch (error: any) {
      console.error('Error saving exclusions:', error);

      // Always try to release the lock on error
      try {
        await new Promise<void>((resolve, reject) => {
          removeLock(
            manualId,
            () => resolve(),
            (lockError) => reject(lockError)
          );
        });
      } catch (lockError) {
        console.error('Failed to release manual lock:', lockError);
      }

      errorHelper.enqueueErrors(error);
    }
    setIsSaving(false);
  }, [manualId, groupId, manualQuery.data, selectedIds, getLock, removeLock, updateManual, queryClient, navigate, backUrl]);

  // Build ToC data structure for getting selected items (with numbering)
  const tocData = useMemo(
    () => (manualQuery.data ? buildToCData(manualQuery.data) : null),
    [manualQuery.data]
  );

  // Get all selected leaf items for the drawer (only policies and individual prefaces, not parent containers)
  const getSelectedItems = useCallback((): ToCItemData[] => {
    if (!tocData) return [];

    const collectSelectedLeaves = (items: ToCItemData[]): ToCItemData[] => {
      const result: ToCItemData[] = [];
      for (const item of items) {
        // Only include leaf items (no children) that are selected
        if (selectedIds.has(item.id) && !item.hasChildren) {
          result.push(item);
        }
        // Always recurse into children to find selected leaves
        if (item.children) {
          result.push(...collectSelectedLeaves(item.children));
        }
      }
      return result;
    };
    return tocData.children ? collectSelectedLeaves(tocData.children) : [];
  }, [tocData, selectedIds]);

  // Handle toggle from drawer (single item deselect)
  const handleDrawerToggle = useCallback(
    (id: string) => {
      const newSelectedIds = new Set(selectedIds);
      newSelectedIds.delete(id);
      setSelectedIds(newSelectedIds);
    },
    [selectedIds]
  );

  if (groupQuery.isError || manualQuery.isError) {
    return (
      <TakeoverLayout backUrl={backUrl} pageTitle={t('groups.manuals.exclusions.pageTitle')}>
        {errorHelper.renderErrors(groupQuery.isError ? groupQuery : manualQuery)}
      </TakeoverLayout>
    );
  }

  if (groupQuery.isLoading || manualQuery.isLoading || !groupQuery.data || !manualQuery.data) {
    return (
      <TakeoverLayout backUrl={backUrl} pageTitle={t('groups.manuals.exclusions.pageTitle')}>
        <Loading />
      </TakeoverLayout>
    );
  }

  const selectedItems = getSelectedItems();
  const hasSelectedItems = selectedItems.length > 0;

  return (
    <TakeoverLayout
      backUrl={backUrl}
      pageTitle={`${t('groups.manuals.exclusions.title')} - ${manualQuery.data.name}`}
      headerProps={{
        title: t('groups.manuals.exclusions.title'),
        subtitle: `${manualQuery.data.name} - ${groupQuery.data.name}`,
        actions: (
          <Stack direction='row' spacing={2}>
            <Button variant='outlined' color='neutral' onClick={handleClose} disabled={isSaving}>
              {t('groups.manuals.exclusions.cancel')}
            </Button>
            <Button
              variant='contained'
              onClick={handleSave}
              disabled={isSaving}
              endIcon={isSaving ? <CircularProgress size={16} /> : null}
            >
              {t('groups.manuals.exclusions.save')}
            </Button>
          </Stack>
        ),
      }}
      rightDrawer={
        hasSelectedItems
          ? {
            header: `${t('groups.manuals.exclusions.selectedPolicies')} (${selectedItems.length})`,
            content: <ToCDrawer selectedItems={selectedItems} onToggle={handleDrawerToggle} disabled={isSaving} />,
            closeable: false,
            noPadding: true,
          }
          : undefined
      }
      rightDrawerOpen={hasSelectedItems}
    >
      <Box sx={{ height: '100%', width: '80%', mx: 'auto' }}>
        <ToCSelector
          manual={manualQuery.data}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          disabled={isSaving}
        />
      </Box>
    </TakeoverLayout>
  );
}